import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { initializeBreakawayHull } from '../../../../store/features/breakawayHullSlice';
import { setAdditionalHull } from '../../../../store/features/shipBuilderSlice';
import { selectors } from '../../../../store/selectors/shipBuilderSelectors';
import { ADDITIONAL_HULLS } from '../../../../constants/hull';
import { Hull } from '../../../../types/ship';
import { formatNumber } from '../../../../utils/formatting';

interface BreakawayConfigurationModalProps {
  isOpen: boolean;
  onClose: () => void;
  shipSize: number;
  onConfigChange: (isBreakaway: boolean) => void;
}

export const BreakawayConfigurationModal: React.FC<BreakawayConfigurationModalProps> = ({
  isOpen,
  onClose,
  shipSize,
  onConfigChange,
}) => {
  const dispatch = useAppDispatch();
  const hull = useAppSelector(selectors.selectHull);
  const additionalHullTypesArray = Object.values(ADDITIONAL_HULLS);

  const [sections, setSections] = useState<Array<{ id: string; name: string; size: number }>>([]);
  const [isEnabled, setIsEnabled] = useState(true); // Always enabled when modal opens

  // Initialize with default sections when enabled
  useEffect(() => {
    if (isEnabled && sections.length === 0) {
      const defaultSections = [
        { id: 'section-1', name: 'Command Module', size: Math.floor(shipSize * 0.5) },
        { id: 'section-2', name: 'Engine Module', size: Math.floor(shipSize * 0.3) },
        { id: 'section-3', name: 'Cargo Module', size: shipSize - Math.floor(shipSize * 0.5) - Math.floor(shipSize * 0.3) },
      ];
      setSections(defaultSections);
    }
  }, [isEnabled, shipSize, sections.length]);



  const handleSectionSizeChange = (sectionId: string, newSize: number) => {
    const updatedSections = sections.map(section => 
      section.id === sectionId ? { ...section, size: newSize } : section
    );
    setSections(updatedSections);
  };

  const handleSectionNameChange = (sectionId: string, newName: string) => {
    const updatedSections = sections.map(section => 
      section.id === sectionId ? { ...section, name: newName } : section
    );
    setSections(updatedSections);
  };

  const handleAddSection = () => {
    const newSection = {
      id: `section-${sections.length + 1}`,
      name: `Module ${sections.length + 1}`,
      size: Math.floor(shipSize / (sections.length + 1)),
    };
    const updatedSections = [...sections, newSection];
    setSections(updatedSections);
  };

  const handleRemoveSection = (sectionId: string) => {
    if (sections.length <= 2) return; // Minimum 2 sections
    const updatedSections = sections.filter(section => section.id !== sectionId);
    setSections(updatedSections);
  };

  const handleSaveSections = () => {
    dispatch(initializeBreakawayHull({ sections }));
    
    // Set the breakaway hull type in the main hull configuration
    if (hull) {
      const breakawayType = additionalHullTypesArray.find(t => t.type === "breakaway");
      if (breakawayType) {
        const updatedHull: Partial<Hull> = {
          ...hull,
          additionalType: {
            type: breakawayType.type,
            name: breakawayType.name,
            description: breakawayType.description,
            costModifier: breakawayType.costModifier ?? 1,
            requirements: breakawayType.requirements ? [...breakawayType.requirements] : [],
            percent: 0,
          },
        };
        dispatch(setAdditionalHull(updatedHull));
      }
    }
    
    onClose();
  };

  const handleCancel = () => {
    // Reset to original state
    setIsEnabled(false);
    onConfigChange(false);
    setSections([]);
    
    // Clear the breakaway hull type if it was set
    if (hull && hull.additionalType?.type === "breakaway") {
      const updatedHull: Partial<Hull> = {
        ...hull,
        additionalType: undefined,
      };
      dispatch(setAdditionalHull(updatedHull));
    }
    
    onClose();
  };

  const totalConfiguredSize = sections.reduce((sum, section) => sum + section.size, 0);
  const sizeDifference = shipSize - totalConfiguredSize;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Breakaway Hulls Configuration</h2>
          <button
            onClick={onClose}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        <div className="space-y-6">
          {/* Header Information */}
          <div className="p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg">
            <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-200">Breakaway Hulls</h3>
            <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
              Configure your ship to operate as multiple independent vessels
            </p>
          </div>

          {/* Rules Section */}
          <div className="bg-blue-100 dark:bg-blue-900/30 border border-blue-300 dark:border-blue-700 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-2">Breakaway Hull Rules</h4>
            <ul className="text-xs text-blue-800 dark:text-blue-300 space-y-1">
              <li>• Each section must have its own bridge and power plant</li>
              <li>• Drives, power plants, and weapons can be combined when calculating performance</li>
              <li>• Each section can operate independently</li>
              <li>• Total section sizes must equal ship size</li>
            </ul>
          </div>

          {/* Section Configuration */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100">Section Configuration</h4>
              <button
                onClick={handleAddSection}
                className="px-4 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
              >
                Add Section
              </button>
            </div>

            {sections.map((section, index) => (
              <div key={section.id} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-white dark:bg-gray-700 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <input
                      type="text"
                      value={section.name}
                      onChange={(e) => handleSectionNameChange(section.id, e.target.value)}
                      className="text-lg font-medium border border-gray-300 dark:border-gray-600 rounded px-2 py-1 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:bg-gray-600 dark:text-gray-100"
                      placeholder="Section Name"
                    />
                    <span className="text-sm text-gray-500 dark:text-gray-400">Section {index + 1}</span>
                  </div>
                  {sections.length > 2 && (
                    <button
                      onClick={() => handleRemoveSection(section.id)}
                      className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 text-sm font-medium"
                    >
                      Remove
                    </button>
                  )}
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Size (tons)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max={shipSize}
                      value={section.size}
                      onChange={(e) => handleSectionSizeChange(section.id, parseInt(e.target.value) || 1)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:bg-gray-600 dark:text-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Percentage
                    </label>
                    <div className="px-3 py-2 bg-gray-50 dark:bg-gray-600 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-gray-100">
                      {formatNumber((section.size / shipSize) * 100)}%
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Summary */}
            <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Total Configured:</span>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{formatNumber(totalConfiguredSize)} tons</span>
              </div>
              <div className="flex justify-between items-center mt-1">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Ship Size:</span>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{formatNumber(shipSize)} tons</span>
              </div>
              <div className="flex justify-between items-center mt-1">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Difference:</span>
                <span className={`text-sm font-medium ${sizeDifference === 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {sizeDifference > 0 ? '+' : ''}{formatNumber(sizeDifference)} tons
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveSections}
            disabled={sizeDifference !== 0}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Save Configuration
          </button>
        </div>
      </div>
    </div>
  );
}; 